/**
 * Equivallent to useTranslation, but with an aditional param
 * that enables overriding all translations to English.
 * Enables component-level localization testing.
 * For a class component alternative, see the withTranslationTest HOC.
 * Changing the language while enableLocalization is false will not trigger a rerender.
 *
 * @param primaryKey Key which scopes all future t() calls, see react-i18next useTranslation.
 * @param enableLocalization When false, only English is considered.
 * @return A translation function and i18next instance.
 */

import i18next, {
  StringMap,
  TFunction,
  TFunctionKeys,
  TOptions,
  i18n,
} from 'i18next';
import { useEffect, useState, useCallback } from 'react';
import { BASE_LOCALE, KeyParam } from 'js/i18next/i18n.d';
import { useTranslation } from 'js/utils/hooks';

// Extend this interface component receives enable via props.
export interface TranslationTest {
  enableLocalization?: boolean;
}

export interface FixedTFunction {
  // basic usage
  <
    TKeys extends TFunctionKeys = string,
    TInterpolationMap extends object = StringMap,
  >(
    key: TKeys | TKeys[],
    options?: TOptions<TInterpolationMap> | string,
  ): string;
  // overloaded usage
  <
    TKeys extends TFunctionKeys = string,
    TInterpolationMap extends object = StringMap,
  >(
    key: TKeys | TKeys[],
    defaultValue?: string,
    options?: TOptions<TInterpolationMap> | string,
  ): string;
}

const useTranslationTest = (
  primaryKey: string,
  enableLocalization: boolean,
): [FixedTFunction, i18n] => {
  const [localizedT] = useTranslation(primaryKey);
  const baseOnlyT = useCallback(
    (key: KeyParam, opts: object) =>
      i18next.getFixedT(BASE_LOCALE, primaryKey)(key, opts),
    [primaryKey],
  );

  const chooseT = useCallback(
    () => (enableLocalization ? localizedT : baseOnlyT),
    [enableLocalization, localizedT, baseOnlyT],
  );
  const [t, setT] = useState<TFunction>(chooseT);

  useEffect(() => setT(chooseT), [enableLocalization, primaryKey, chooseT]);

  return [t, i18next];
};

export default useTranslationTest;
